import React, { useCallback, useState, createContext, useContext } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Toast } from '../components/ui/Toast';
import { Alert } from '../components/ui/Alert';
import { Notification } from '../components/ui/Notification';
import { LanguageContext } from '../App';
// Types for our notifications
export type NotificationType = 'success' | 'error' | 'warning' | 'info' | 'neutral';
export type NotificationPosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
export interface NotificationProps {
  id: string;
  title: string;
  message?: string;
  type: NotificationType;
  duration?: number | null; // null means it won't auto-dismiss
  position?: NotificationPosition;
  onAction?: () => void;
  actionText?: string;
  variant?: 'toast' | 'notification' | 'alert';
  isOpen?: boolean;
}
interface NotificationContextType {
  notifications: NotificationProps[];
  toast: (props: Omit<NotificationProps, 'id' | 'variant'>) => string;
  notify: (props: Omit<NotificationProps, 'id' | 'variant'>) => string;
  alert: (props: Omit<NotificationProps, 'id' | 'variant'>) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}
const NotificationContext = createContext<NotificationContextType | undefined>(undefined);
export const NotificationProvider: React.FC<{
  children: ReactNode;
}> = ({
  children
}) => {
  const [notifications, setNotifications] = useState<NotificationProps[]>([]);
  const {
    direction
  } = useContext(LanguageContext);
  // Generate default position based on direction
  const getDefaultPosition = useCallback((variant: 'toast' | 'notification' | 'alert'): NotificationPosition => {
    if (variant === 'toast') {
      return direction === 'rtl' ? 'bottom-left' : 'bottom-right';
    }
    return direction === 'rtl' ? 'top-left' : 'top-right';
  }, [direction]);
  const createNotification = useCallback((props: Omit<NotificationProps, 'id' | 'variant'>, variant: 'toast' | 'notification' | 'alert'): string => {
    const id = Math.random().toString(36).substring(2, 9);
    const position = props.position || getDefaultPosition(variant);
    const newNotification: NotificationProps = {
      ...props,
      id,
      variant,
      position,
      isOpen: true
    };
    setNotifications(prev => [...prev, newNotification]);
    // Auto-dismiss if duration is provided
    if (props.duration !== null && props.duration !== undefined) {
      setTimeout(() => {
        dismiss(id);
      }, props.duration);
    }
    return id;
  }, [getDefaultPosition]);
  const toast = useCallback((props: Omit<NotificationProps, 'id' | 'variant'>) => createNotification({
    ...props,
    duration: props.duration ?? 3000
  }, 'toast'), [createNotification]);
  const notify = useCallback((props: Omit<NotificationProps, 'id' | 'variant'>) => createNotification({
    ...props,
    duration: props.duration ?? null
  }, 'notification'), [createNotification]);
  const alert = useCallback((props: Omit<NotificationProps, 'id' | 'variant'>) => createNotification({
    ...props,
    duration: props.duration ?? null
  }, 'alert'), [createNotification]);
  const dismiss = useCallback((id: string) => {
    setNotifications(prev => prev.map(notification => notification.id === id ? {
      ...notification,
      isOpen: false
    } : notification));
    // Remove from state after animation completes
    setTimeout(() => {
      setNotifications(prev => prev.filter(notification => notification.id !== id));
    }, 300);
  }, []);
  const dismissAll = useCallback(() => {
    setNotifications(prev => prev.map(notification => ({
      ...notification,
      isOpen: false
    })));
    // Remove all after animation completes
    setTimeout(() => {
      setNotifications([]);
    }, 300);
  }, []);
  // Group notifications by position
  const notificationsByPosition: Record<NotificationPosition, NotificationProps[]> = {
    'top-right': [],
    'top-left': [],
    'bottom-right': [],
    'bottom-left': [],
    'top-center': [],
    'bottom-center': []
  };
  notifications.forEach(notification => {
    const position = notification.position || 'bottom-right';
    notificationsByPosition[position].push(notification);
  });
  return <NotificationContext.Provider value={{
    notifications,
    toast,
    notify,
    alert,
    dismiss,
    dismissAll
  }} data-id="element-781">
      {children}
      {/* Render notifications grouped by position */}
      {Object.entries(notificationsByPosition).map(([position, items]) => <div key={position} className={`fixed z-50 flex flex-col gap-2 p-4 max-w-md w-full pointer-events-none
            ${position.includes('top') ? 'top-0' : 'bottom-0'}
            ${position.includes('right') ? 'right-0' : ''}
            ${position.includes('left') ? 'left-0' : ''}
            ${position.includes('center') ? 'left-1/2 transform -translate-x-1/2' : ''}
          `} data-id="element-782">
          <AnimatePresence data-id="element-783">
            {items.map(notification => {
          const {
            variant = 'toast',
            ...props
          } = notification;
          if (variant === 'toast') {
            return <Toast key={notification.id} {...props} onDismiss={() => dismiss(notification.id)} data-id="element-784" />;
          }
          if (variant === 'notification') {
            return <Notification key={notification.id} {...props} onDismiss={() => dismiss(notification.id)} data-id="element-785" />;
          }
          if (variant === 'alert') {
            return <Alert key={notification.id} {...props} onDismiss={() => dismiss(notification.id)} data-id="element-786" />;
          }
          return null;
        })}
          </AnimatePresence>
        </div>)}
    </NotificationContext.Provider>;
};
// Custom hook to use the notification context
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};